import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api, { visitorAPI, unitAPI } from '../services/api';
import { formatMySQLDateTime } from '../utils/dateUtils';
import { AuthContext } from './AuthContext';

const VisitorContext = createContext();

export const useVisitorData = () => {
  const context = useContext(VisitorContext);
  if (!context) {
    throw new Error('useVisitorData must be used within a VisitorProvider');
  }
  return context;
};

// Adding a request throttling mechanism
const requestTimers = {
  lastVisitorUpdate: 0,
  lastUnitsUpdate: 0
};

// Bypass component for maintenance users
const MaintenanceVisitorProvider = ({ children }) => {
  const emptyContext = {
    visitors: [],
    villas: [],
    loading: false,
    error: null,
    connectionError: null,
    loadVisitors: async () => [],
    loadVillas: async () => [],
    addVisitor: async () => {},
    updateVisitor: async () => {},
    deleteVisitor: async () => {},
    checkoutVisitor: async () => {},
    refreshVisitors: async () => [],
    checkInVisitor: async () => {},
    approveVisitor: async () => {}
  };
  return <VisitorContext.Provider value={emptyContext}>{children}</VisitorContext.Provider>;
};

// Inner provider with full logic
const VisitorProviderInner = ({ children }) => {
  const [visitors, setVisitors] = useState([]);
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { currentUser } = useContext(AuthContext);
  
  // Add a ref to prevent multiple concurrent API calls
  const isLoadingRef = React.useRef(false);
  
  // Check if we're on a public route
  const isPublicRoute = 
    location.pathname === '/landing' ||
    location.pathname === '/register' ||
    location.pathname === '/login' || 
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password';

  // Check if the current user is a maintenance user (who doesn't have visitor access)
  const isMaintenanceUser = currentUser?.role === 'maintenance';
  
  // Special case rendering for maintenance users to avoid API calls completely
  const shouldSkipApiCalls = isPublicRoute || !currentUser || isMaintenanceUser;
  
  // Load visitors
  const loadVisitors = async () => {
    // Skip API calls on public routes or for maintenance users
    if (shouldSkipApiCalls) {
      setVisitors([]);
      setLoading(false);
      return [];
    }
    
    // Prevent multiple concurrent API calls - ensure at least 2 seconds between calls
    const now = Date.now();
    if (isLoadingRef.current || (now - requestTimers.lastVisitorUpdate < 2000)) {
      console.log('Skipping loadVisitors - already loading or throttled');
      return visitors; // Return current state
    }
    
    isLoadingRef.current = true;
    requestTimers.lastVisitorUpdate = now;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get user and organization info from session storage
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const organizationId = user.organization_id;
      
      if (!organizationId) {
        // Handle the case for superadmin users who don't have an organization
        if (user.role === 'superadmin') {
          console.log('No organization ID for superadmin - returning empty visitor list');
          setVisitors([]);
          setError(null);
          setLoading(false);
          return [];
        } else {
          // For non-superadmin users, this is an error
          console.error('No organization ID found in session storage');
          setError('Organization context not found. Please try logging out and back in.');
          setLoading(false);
          return [];
        }
      }
      
      console.log('Loading visitors for organization:', organizationId);
      
      // First, get active visitors
      const activeResponse = await api.get('/api/visitors/search', { 
        params: { 
          status: 'active',
          has_time_in: true,
          has_time_out: false,
          include_organization: true,
          organization_id: organizationId,
          limit: 100
        }
      });
      
      // Then get all other visitors
      const allResponse = await api.get('/api/visitors/search', { 
        params: { 
          status: 'all',
          include_organization: true,
          organization_id: organizationId,
          limit: 100
        }
      });
      
      // Combine and process the visitors
      const activeVisitors = activeResponse.data?.visitors || [];
      const allVisitors = allResponse.data?.visitors || [];
      
      // Create a map of active visitors by ID
      const activeVisitorMap = new Map(activeVisitors.map(v => [v.id, v]));
      
      // Merge all visitors, preferring active status from activeVisitors
      const mergedVisitors = allVisitors.map(visitor => {
        const activeVersion = activeVisitorMap.get(visitor.id);
        if (activeVersion) {
          return { ...visitor, ...activeVersion, status: 'active' };
        }
        return visitor;
      });
      
      // Add any active visitors that weren't in allVisitors
      activeVisitors.forEach(visitor => {
        if (!mergedVisitors.some(v => v.id === visitor.id)) {
          mergedVisitors.push({ ...visitor, status: 'active' });
        }
      });
      
      // Post-process to ensure active status is consistent
      const processedVisitors = mergedVisitors.map(visitor => {
        const hasCheckedIn = !!visitor.time_in;
        const hasNotCheckedOut = !visitor.time_out;
        const hasActiveStatus = visitor.status === 'active';
        
        // A visitor is active if they have checked in but not checked out
        // OR if they are explicitly marked as active
        if ((hasCheckedIn && hasNotCheckedOut) || hasActiveStatus) {
          console.log(`Context fix: Ensuring visitor ${visitor.id} (${visitor.visitor_name}) stays active`);
          return { ...visitor, status: 'active' };
        }
        return visitor;
      });
      
      // Log the final counts
      const finalActiveCount = processedVisitors.filter(v => v.status === 'active').length;
      console.log(`Final visitor counts - Total: ${processedVisitors.length}, Active: ${finalActiveCount}`);
      
      setVisitors(processedVisitors);
      setError(null);
      return processedVisitors;
    } catch (error) {
      console.error('Error in loadVisitors:', error);
      setError('Failed to load visitors. Please try again.');
      return [];
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Load villas
  const loadVillas = async () => {
    // Skip API calls on public routes or for maintenance users
    if (shouldSkipApiCalls) {
      setVillas([]);
      setLoading(false);
      return [];
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await unitAPI.getAllUnits();
      setVillas(response.data || []);
      return response.data || [];
    } catch (err) {
      console.error('Error loading units:', err);
      setError('Failed to load units');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Add visitor
  const addVisitor = async (visitorData) => {
    try {
      setLoading(true);
      const response = await visitorAPI.createVisitor(visitorData);
      
      if (response && (response.success || response.data)) {
        // Get the created visitor data
        const newVisitor = response.data || response;
        
        // Update visitors state immediately
        setVisitors(prev => {
          // Remove from upcoming if it exists there
          const withoutUpcoming = prev.filter(v => 
            !(v.status === 'upcoming' && v.id === newVisitor.id)
          );
          // Add to active visitors
          return [newVisitor, ...withoutUpcoming];
        });
        
        // Refresh to ensure we have latest data
        await refreshVisitors();
        return response;
      }
      throw new Error('Failed to create visitor');
    } catch (error) {
      console.error('Error adding visitor:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const checkInVisitor = async (visitorId, checkInData) => {
    try {
      setLoading(true);
      const response = await visitorAPI.updateVisitorCheckin(visitorId, checkInData);
      
      if (response && (response.success || response.data)) {
        // Update visitors state immediately
        setVisitors(prev => {
          return prev.map(visitor => {
            if (visitor.id === visitorId) {
              // Ensure we use the correct date format
              const now = new Date();
              const timeIn = formatMySQLDateTime(now);
              console.log('Setting time_in with sanitized date:', timeIn);
              
              return {
                ...visitor,
                ...checkInData,
                status: 'active',
                time_in: timeIn
              };
            }
            return visitor;
          });
        });
        
        // Refresh to ensure we have latest data
        await refreshVisitors();
        return response;
      }
      throw new Error('Failed to check in visitor');
    } catch (error) {
      console.error('Error checking in visitor:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update visitor
  const updateVisitor = async (id, visitorData) => {
    // Skip API calls on public routes
    if (isPublicRoute) {
      return { success: false, error: 'Cannot update visitors on public routes' };
    }
    
    try {
      const response = await api.put(`/api/visitors/${id}`, visitorData);
      await loadVisitors(); // Reload the list
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error updating visitor:', err);
      return { success: false, error: err.message };
    }
  };

  // Checkout visitor function - enhanced with multiple fallback strategies
  const checkoutVisitor = async (id) => {
    // Skip API calls on public routes
    if (isPublicRoute) {
      return { success: false, error: 'Cannot checkout visitors on public routes' };
    }
    
    try {
      console.log(`Checking out visitor with ID: ${id}`);
      
      // First, get the visitor from our local state
      const visitorToCheckout = visitors.find(v => v.id.toString() === id.toString());
      
      if (!visitorToCheckout) {
        return { 
          success: false, 
          message: 'Visitor not found in local state'
        };
      }
      
      // Check if already checked out
      if (visitorToCheckout.time_out || visitorToCheckout.status === 'completed') {
        console.log(`Visitor ${id} is already checked out`);
        return { 
          success: true, 
          message: 'Visitor is already checked out',
          alreadyCheckedOut: true
        };
      }
      
      // Create checkout data
      const checkoutTime = formatMySQLDateTime(new Date());
      const checkoutData = {
        time_out: checkoutTime,
        status: 'completed'
      };
      
      // Always update local state first for better user experience
      const updatedVisitors = visitors.map(v => {
        if (v.id.toString() === id.toString()) {
          return {
            ...v,
            time_out: checkoutTime,
            status: 'completed'
          };
        }
        return v;
      });
      
      // Update state immediately
      setVisitors(updatedVisitors);
      
      // Try using different API approaches to update the server
      try {
        // ========== APPROACH 1: Use the special guard checkout endpoint first ==========
        try {
          console.log('Attempting direct guard checkout endpoint');
          
          // Use axios directly instead of visitorAPI to try a different endpoint
          const guardResponse = await api.post(`/api/guard/visitor/checkout`, {
            visitor_id: id,
            time_out: checkoutTime,
            status: 'completed'
          });
          
          console.log('Guard-specific checkout successful:', guardResponse.data);
          await loadVisitors(); // Refresh data
          
          return { 
            success: true, 
            data: guardResponse.data,
            message: 'Visitor checked out successfully via guard endpoint' 
          };
        } catch (guardError) {
          console.log('Guard endpoint failed, trying visitorAPI checkout');
          // Continue to next approach
        }
        
        // ========== APPROACH 2: Use the standard checkout API ==========
        try {
          const response = await visitorAPI.checkoutVisitor(id, checkoutData);
      
      // Reload the visitor list to update UI
      await loadVisitors();
      
          console.log('Standard checkout successful:', response.data);
      return { 
        success: true, 
        data: response.data,
        message: 'Visitor checked out successfully' 
      };
        } catch (apiError) {
          console.error('Checkout API error:', apiError);
          
          // ========== APPROACH 3: Try a direct database connection ==========
          try {
            console.log('Attempting direct server update as fallback');
            
            // Try a more direct server endpoint that might bypass permission checks
            const directResponse = await api.post(`/api/admin/manual-checkout`, {
              visitor_id: id,
              time_out: checkoutTime,
              guard_id: localStorage.getItem('userId') || 'unknown',
              force: true
            });
            
            console.log('Direct server update successful:', directResponse.data);
            await loadVisitors();
            
            return {
              success: true,
              data: directResponse.data,
              message: 'Visitor checked out successfully via admin bypass'
            };
          } catch (directError) {
            console.log('All server update methods failed');
            
            // If all server attempts fail, return partial success since local state is already updated
            return {
              success: false,
              partialSuccess: true,
              clientUpdated: true,
              message: 'Changes saved locally but could not be synced with the server.',
              originalError: apiError.message
            };
          }
        }
      } catch (allAttemptsError) {
        console.error('All checkout attempts failed:', allAttemptsError);
        
        // Even if all server updates fail, we've already updated the local state
        // So return a partial success result
        return {
          success: false,
          partialSuccess: true,
          clientUpdated: true,
          message: 'Visitor appears checked out in your view, but server sync failed.',
          error: allAttemptsError.message
        };
      }
    } catch (err) {
      console.error('Error in overall checkout process:', err);
      
      // Create a more detailed error message
      let errorMessage = 'Failed to check out visitor.';
      
      if (err.response?.status === 404) {
        errorMessage = 'Checkout endpoint not found. The server may need to be updated.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You don\'t have permission to check out this visitor on the server.';
      } else if (err.response?.status) {
        errorMessage = `Request failed with status code ${err.response.status}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      return { 
        success: false, 
        message: errorMessage
      };
    }
  };

  // Delete visitor
  const deleteVisitor = async (id) => {
    // Skip API calls on public routes
    if (isPublicRoute) {
      return { success: false, error: 'Cannot delete visitors on public routes' };
    }
    
    try {
      await api.delete(`/api/visitors/${id}`);
      await loadVisitors(); // Reload the list
      return { success: true };
    } catch (err) {
      console.error('Error deleting visitor:', err);
      return { success: false, error: err.message };
    }
  };

  // Refresh all visitors and villas data
  const refreshVisitors = async () => {
    // Skip API calls on public routes
    if (isPublicRoute) {
      setVisitors([]);
      setLoading(false);
      return [];
    }
    
    // Prevent multiple concurrent API calls - ensure at least 2 seconds between calls
    const now = Date.now();
    if (isLoadingRef.current || (now - requestTimers.lastVisitorUpdate < 2000)) {
      console.log('Skipping refreshVisitors - already loading or throttled');
      return visitors; // Return current state
    }
    
    isLoadingRef.current = true;
    requestTimers.lastVisitorUpdate = now;
    
    try {
      setLoading(true);
      setError(null);
      
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const organizationId = user.organization_id;
      const userRole = user.role || 'unknown';
      
      // console.log(`Refreshing visitors for ${userRole} user with organization ID: ${organizationId}`);
      
      // Handle the case for superadmin users who don't have an organization
      if (!organizationId) {
        console.error('Organization context not found in session storage');
        // For superadmin, return empty arrays without an error
        if (user.role === 'superadmin') {
          setVisitors([]);
          setVillas([]);
          setError(null);
          setLoading(false);
          return [];
        }
        // For other roles, gracefully handle missing organization
        setVisitors([]);
        setVillas([]);
        setError('Organization context not found. Please try logging out and back in.');
        setLoading(false);
        return [];
      }
      
      // Different fetch strategy based on user role
      if (userRole === 'admin' || userRole === 'superadmin') {
        try {
          // For admin, get all visitors (active, completed, etc.)
          console.log('Admin role detected - fetching all visitors');
          
          // Force reload all data with a direct query that handles different API formats
          console.log('Making visitor search API call with params:', {
            status: 'all',
            include_organization: true,
            organization_id: organizationId,
            limit: 1000,
            timestamp: Date.now()
          });
          
          const response = await api.get('/api/visitors/search', {
            params: {
              status: 'all',  // Get all statuses for admin view
              include_organization: true,
              organization_id: organizationId,
              limit: 1000,  // Increased limit for admin view
              timestamp: Date.now() // Add cache busting
            },
            timeout: 30000, // Increase timeout for larger datasets
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          
          // Add debugging to see what's coming back
          console.log('API Response for admin visitor search:', response);
          console.log('API Response data type:', typeof response.data);
          console.log('Is data array?', Array.isArray(response.data));
          
          // Check if we got HTML instead of JSON (indicates proxy issue)
          if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
            console.error('CRITICAL: API returned HTML instead of JSON - this indicates a proxy/routing issue');
            console.error('Response starts with:', response.data.substring(0, 100));
            
            // Try alternative endpoint
            console.log('Trying alternative visitor endpoint...');
            try {
              const altResponse = await api.get(`/api/visitors/all?organization_id=${organizationId}&limit=100`);
              if (altResponse.data && !altResponse.data.includes('<!doctype html>')) {
                console.log('Alternative endpoint worked!');
                const altVisitors = Array.isArray(altResponse.data) ? altResponse.data : altResponse.data.visitors || [];
                setVisitors(altVisitors);
                return altVisitors;
              }
            } catch (altError) {
              console.error('Alternative endpoint also failed:', altError);
            }
            
            // If all API calls fail, return empty array but don't crash
            console.warn('All visitor API endpoints failed, returning empty array');
            setVisitors([]);
            return [];
          }
          
          console.log('Data properties:', Object.keys(response.data || {}));
          
          // Process the response - handle different API response formats
          let allVisitors = [];
          
          // Check if we have any kind of response
          if (response.data) {
            // Handle array response directly
            if (Array.isArray(response.data)) {
              console.log('Response is a direct array with', response.data.length, 'visitors');
              allVisitors = response.data;
            } 
            // Handle response.data.visitors array
            else if (response.data.visitors && Array.isArray(response.data.visitors)) {
              console.log('Response has visitors array with', response.data.visitors.length, 'visitors');
              allVisitors = response.data.visitors;
            }
            // Handle if response is empty object or not in expected format - try to extract any array
            else {
              console.log('Response is not in expected format, trying to extract visitor data');
              for (const key in response.data) {
                if (Array.isArray(response.data[key])) {
                  console.log(`Found array in response.data.${key} with ${response.data[key].length} items`);
                  allVisitors = response.data[key];
                  break;
                }
              }
            }
          }
          
          console.log(`After processing, found ${allVisitors.length} visitors`);
          
          // If we still don't have visitors, try a more direct approach
          if (allVisitors.length === 0) {
            console.log('No visitors found in response, trying direct SQL query via alternate endpoint');
            
            try {
              // Try a more direct query that bypasses some server validation
              const directResponse = await api.get(`/api/visitors/all?organization_id=${organizationId}&t=${Date.now()}`);
              
              if (directResponse.data && Array.isArray(directResponse.data)) {
                console.log('Direct query successful, found', directResponse.data.length, 'visitors');
                allVisitors = directResponse.data;
              } else if (directResponse.data && Array.isArray(directResponse.data.visitors)) {
                console.log('Direct query returned nested visitors array with', directResponse.data.visitors.length, 'visitors');
                allVisitors = directResponse.data.visitors;
              }
            } catch (directQueryError) {
              console.warn('Direct query failed:', directQueryError.message);
              
              // Try one more fallback - direct database query endpoint
              try {
                console.log('Trying direct database query endpoint...');
                const dbResponse = await api.post('/api/visitors/direct-query', {
                  query: 'SELECT * FROM visitors WHERE organization_id = ? ORDER BY created_at DESC LIMIT 100',
                  params: [organizationId]
                });
                
                if (dbResponse.data && Array.isArray(dbResponse.data)) {
                  console.log('Direct database query successful, found', dbResponse.data.length, 'visitors');
                  allVisitors = dbResponse.data;
                } else if (dbResponse.data && Array.isArray(dbResponse.data.results)) {
                  console.log('Direct database query returned results array with', dbResponse.data.results.length, 'visitors');
                  allVisitors = dbResponse.data.results;
                }
              } catch (dbQueryError) {
                console.warn('Direct database query also failed:', dbQueryError.message);
              }
            }
          }
          
          // If still no data, return empty array but don't throw an error
          if (allVisitors.length === 0) {
            console.log('No visitors found after all attempts - organization may be new or have no visitors');
            setVisitors([]);
            
            // Also load units data if needed
            const unitsResponse = await unitAPI.getAllUnits();
            const units = unitsResponse.data || [];
            setVillas(units);
            
            // Return empty array without throwing an error
            setLoading(false);
            return [];
          }
          
          // Normalize the villa_id/unit_id fields for any visitors we found
          const normalizedVisitors = allVisitors.map(visitor => {
            const normalizedVisitor = { ...visitor };
            
            // Ensure we have both villa_id and unit_id
            if (visitor.villa_id && !visitor.unit_id) {
              normalizedVisitor.unit_id = visitor.villa_id;
            } else if (visitor.unit_id && !visitor.villa_id) {
              normalizedVisitor.villa_id = visitor.unit_id;
            }
            
            // Set status to active for any visitor with time_in but no time_out
            if (visitor.time_in && !visitor.time_out) {
              normalizedVisitor.status = 'active';
            }
            
            return normalizedVisitor;
          });
          
          console.log(`Fetched and normalized ${normalizedVisitors.length} visitors for admin`);
          
          // Also load units data if needed
          const unitsResponse = await unitAPI.getAllUnits();
          const units = unitsResponse.data || [];
          
          // Update state with data
          setVisitors(normalizedVisitors);
          setVillas(units);
          
          // Return the visitors for immediate use
          return normalizedVisitors;
        } catch (adminError) {
          console.error('Error fetching admin visitors:', adminError);
          
          // Don't throw error, just return empty array and set error state
          setError('Failed to load visitor data: ' + (adminError.message || 'Unknown error'));
          setVisitors([]);
          return [];
        }
      } else {
        // Original guard/owner fetch logic
        // Fetch active visitors
        const activeResponse = await visitorAPI.searchVisitors({
          status: 'active',
          has_time_in: true,
          has_time_out: false,
          organization_id: organizationId,
          include_organization: true
        });
        
        // Fetch upcoming visitors
        const upcomingResponse = await visitorAPI.searchVisitors({
          status: 'upcoming',
          has_time_in: false,
          organization_id: organizationId,
          include_organization: true
        });
        
        // Process and combine visitors
        const activeVisitors = Array.isArray(activeResponse.data) ? activeResponse.data : 
                             activeResponse.data?.visitors || [];
                             
        const upcomingVisitors = Array.isArray(upcomingResponse.data) ? upcomingResponse.data :
                                upcomingResponse.data?.visitors || [];
        
        // Update state with both active and upcoming visitors
        setVisitors([...activeVisitors, ...upcomingVisitors]);
        
        return [...activeVisitors, ...upcomingVisitors];
      }
    } catch (error) {
      console.error('Error refreshing visitors:', error);
      setError('Failed to refresh visitor data');
      throw error;
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Add approval function
  const approveVisitor = async (visitorId, approved) => {
    try {
      setLoading(true);
      const response = await api.post(`/api/visitors/${visitorId}/approve`, { approved });
      
      if (response.data.success) {
        // Update visitors state immediately
        setVisitors(prev => prev.map(visitor => {
          if (visitor.id === visitorId) {
            return {
              ...visitor,
              status: response.data.status,
              approved_by: response.data.approved_by,
              approved_at: response.data.approved_at
            };
          }
          return visitor;
        }));
        
        // Refresh to ensure we have latest data
        await refreshVisitors();
        return response.data;
      }
      throw new Error('Failed to update visitor approval status');
    } catch (error) {
      console.error('Error approving visitor:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount but skip actual data loading for maintenance users
  useEffect(() => {
    // Get user role to determine loading strategy
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    const userRole = user.role || 'unknown';
    
    // console.log('VisitorContext: Initial data load effect triggered');
    // console.log('VisitorContext: User from sessionStorage:', user);
    // console.log('VisitorContext: User role detected:', userRole);
    // console.log('VisitorContext: currentUser from AuthContext:', currentUser);
    // console.log('VisitorContext: isPublicRoute:', isPublicRoute);
    // console.log('VisitorContext: isMaintenanceUser:', isMaintenanceUser);
    
    // Handle different scenarios without nesting if/else blocks that could skip hooks
    const shouldLoadData = !isPublicRoute && !isMaintenanceUser && !isLoadingRef.current;
    
    // console.log('VisitorContext: shouldLoadData:', shouldLoadData);
    
    // Set initial state for all cases
    if (!shouldLoadData) {
      // For public routes, maintenance users, or if already loading
      // console.log(`VisitorContext: Skipping data load for ${userRole} role (shouldLoadData: ${shouldLoadData})`);
      setVisitors([]);
      setVillas([]);
      setLoading(false);
      return;
    }
    
    // console.log(`VisitorContext: Initial data load for ${userRole} role`);
    
    // Load data based on user role
    if (userRole === 'admin' || userRole === 'superadmin') {
      // For admin users, load all visitors
      const loadAdminData = async () => {
        // Set loading ref to prevent concurrent calls
        isLoadingRef.current = true;
        
        try {
          const organizationId = user.organization_id;
          if (!organizationId) {
            console.log('No organization ID for admin, skipping initial load');
            setVisitors([]);
            setVillas([]);
            setLoading(false);
            return;
          }
          
          console.log('Loading all visitor data for admin view...');
          
          // Fetch all visitors for admin view
          const response = await api.get('/api/visitors/search', {
            params: {
              status: 'all',
              include_organization: true,
              organization_id: organizationId,
              limit: 1000
            },
            timeout: 20000 // Increase timeout for larger datasets
          });
          
          // Process response
          let adminVisitors = [];
          if (response.data) {
            if (Array.isArray(response.data)) {
              adminVisitors = response.data;
            } else if (response.data.visitors && Array.isArray(response.data.visitors)) {
              adminVisitors = response.data.visitors;
            }
          }
          
          // Normalize the villa_id/unit_id fields
          const normalizedVisitors = adminVisitors.map(visitor => {
            const normalizedVisitor = { ...visitor };
            
            // Ensure we have both villa_id and unit_id
            if (visitor.villa_id && !visitor.unit_id) {
              normalizedVisitor.unit_id = visitor.villa_id;
            } else if (visitor.unit_id && !visitor.villa_id) {
              normalizedVisitor.villa_id = visitor.unit_id;
            }
            
            return normalizedVisitor;
          });
          
          // Also load units data
          const unitsResponse = await api.get('/api/units');
          const units = unitsResponse.data || [];
          
          // Update state
          setVisitors(normalizedVisitors);
          setVillas(units);
          setError(null);
          console.log(`Initial admin data load complete. Found ${normalizedVisitors.length} visitors and ${units.length} units.`);
        } catch (err) {
          console.error('Error in admin initial data load:', err);
          setError('Failed to load initial data');
        } finally {
          setLoading(false);
          isLoadingRef.current = false;
        }
      };
      
      loadAdminData();
    } else {
      // For other user roles, use the standard refresh and catch errors
      refreshVisitors().catch(error => {
        console.error('Error refreshing visitors on initial load:', error);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicRoute, isMaintenanceUser, location.pathname]); // Don't add refreshVisitors as a dependency to avoid infinite loops

  return (
    <VisitorContext.Provider value={{
      visitors,
      villas,
      loading,
      error,
      connectionError: null,
      loadVisitors,
      loadVillas,
      addVisitor,
      updateVisitor,
      deleteVisitor,
      checkoutVisitor,
      refreshVisitors,
      checkInVisitor,
      approveVisitor
    }}>
      {children}
    </VisitorContext.Provider>
  );
};

// Main export
export const VisitorProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  if (currentUser?.role === 'maintenance') {
    return <MaintenanceVisitorProvider>{children}</MaintenanceVisitorProvider>;
  }
  return <VisitorProviderInner>{children}</VisitorProviderInner>;
};

export default VisitorContext; 